import _ from "lodash"
import { observer } from "mobx-react-lite"
import moment from "moment"
import { Spinner, View } from "native-base"
import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { Appearance, ViewStyle } from "react-native"
import {
  Image,
  FormControl,
  FormControlProps,
  JalaWebView,
  LazyRender,
  ModelPicker,
  ModelPickerProps,
  Screen,
  Text,
  JalaLoading,
} from "../../../components"
import { Cycle, Farm, Pond } from "../../../database"
import { translate, TxKeyPath } from "../../../i18n"
import { useStores } from "../../../models"
import { FarmStoreModel } from "../../../models/app-store/farm-store/farm-store"
import { PondStoreModel } from "../../../models/app-store/farm-store/pond-store/pond-store"
import { color } from "../../../theme"
import { createAxios } from "../../../utils/axios"
import config from "../../../utils/config"
import { delay } from "../../../utils/delay"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  flexGrow: 1,
}

const FORM_DROPDOWN: ViewStyle = {
  paddingHorizontal: 16,
  marginBottom: 8
}

const POND_CONTAINER: ViewStyle = {
  flexDirection: 'row',
}

const IMAGE = {
  resizeMode: "contain",
  width: 150,
  height: 150,
  marginBottom: 16,
}

const { BASE_URL } = config();


const ModelField = (
  { name, colorScheme, formControlProps, modelPickerProps }:
  { name?: string, colorScheme?: "light" | "dark", formControlProps?: FormControlProps, modelPickerProps?: (Omit<ModelPickerProps, "onValueChange"> & { onValueChange?: (value, index, item) => void }) },
) => (
  <FormControl
    labelTx={`waterQualityMultiMeasurementForm.${_.camelCase(name)}` as TxKeyPath}
    colorScheme={colorScheme}
    labelColor={color.palette.gray800}
    style={FORM_DROPDOWN}
    {...formControlProps}
  >
     <ModelPicker
        colorScheme={colorScheme}
        placeholderTx={`waterQualityMultiMeasurementForm.${_.camelCase(name)}Placeholder` as TxKeyPath}
        {...modelPickerProps}
      />
  </FormControl>
)

const LoadingState: FC<{ colorScheme: "light"|"dark" }> = function(props) {
  return (
    <View flex={1} px="16px" justifyContent="center" alignItems="center">
      <JalaLoading />
    </View>
  )
}

const EmptyState: FC<{colorScheme: "light" | "dark"}> = (props) => {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const image = require("./chart_data_empty.png")
  return (
    <View flex={1} px="16px" justifyContent="center" alignItems="center">
      <Image source={image} style={IMAGE} />
      <Text tx="graphScreen.message" preset="title3" textAlign="center" color={color.palette.gray900} colorScheme={colorScheme} />
    </View>
  )
}

export const GraphScreen: FC<{ colorScheme: "light" | "dark" }> = observer(function GraphScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;

  // Pull in one of our MST stores
  const { appStore, appStore: { farmId, farmStore } } = useStores();

  const { initiating, pondStore, pondId } = farmStore || FarmStoreModel.create({});
  const { queryingCycle, cycleId } = pondStore || PondStoreModel.create({});

  const [ url, setUrl ] = useState<string>(null);

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: navigation - graph');
  }, []))

  useEffect(() => {
    if (pondStore) pondStore.initWithLastCycle()
  },[pondStore])

  const getUri = useCallback((id, cache) => {
    const uri = `${BASE_URL}/web_view/cycles/${id}/charts?cache_key=${cache}`;
    setUrl(uri);
  }, [])

  const getCacheToken = useCallback(async (id) => {
    return createAxios().post('request_access')
    .then(response => {
      const cache = response.data.data.cache_key;
      getUri(id || null, cache)
    })
  }, [getUri])

  useEffect(() => {
    // getCacheToken(cycleId);
    if (pondStore) getCacheToken(cycleId);
  }, [cycleId, getCacheToken, pondStore])

  const source = useMemo(() => url, [url])

  const onFarmChange = useCallback(async (value) => {
    appStore.set('farmId', value || null)
    await delay(1)
    if (appStore.farmStore) appStore.farmStore.initWithLastBatchAndWaterQualityMultiMeasurements()
  }, [appStore])

  const onPondChange = useCallback(async (value) => {
    farmStore.set('pondId', value || null);
    await delay(1);
    if (farmStore) farmStore.initWithLastBatchAndWaterQualityMultiMeasurements();
  }, [farmStore])

  const onCycleChange = useCallback(async (value) => {
    pondStore.set('cycleId', value);
    await delay(1);
    if (pondStore.cycleId) pondStore.initWithLastCycle();
  }, [pondStore])

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === "light" ? "light-content" : "dark-content"}
      hideOnBlurred={false}
    >
      <ModelField
        colorScheme={colorScheme}
        name='farm_id'
        modelPickerProps={{
          Model: Farm,
          value: farmId,
          onValueChange: onFarmChange,
          isDisabled: initiating,
          InputRightElement: initiating && <Spinner mr="9px"></Spinner>,
          titleTx: "cycleForm.farmIdPlaceholder",
          isUseSearch: true
        }}
      />
      <View style={POND_CONTAINER}>
        <FormControl
          colorScheme={colorScheme}
          labelTx="waterQualityMultiMeasurementForm.pondId"
          style={FORM_DROPDOWN}
          flex={1}
          isDisabled={!farmId}
        >
          <ModelPicker
            key={`${farmId}-pond_id`}
            colorScheme={colorScheme}
            actionSheetColorScheme={colorScheme}
            placeholderTx="cycleForm.pondIdPlaceholder"
            Model={Pond}
            dataLabel={'name'}
            dataKey={'id'}
            fetchData={(model, search) => (
              Promise.resolve(_.orderBy(
                farmStore.pondStoreModels.filter(pondStore => initiating || pondStore.cycles.length > 0).map(pondStore => pondStore.pond)
              )).then((models) => models.filter((m) => (
                `${m.name}`
              ).toLowerCase().includes((search || '').toLowerCase())))
            )}
            value={!!pondId && (pondId || null)}
            onValueChange={onPondChange}
            // isDisabled={initiating}
            isDisabled={(initiating || !farmId)}
            InputRightElement={initiating && <Spinner mr="9px"></Spinner>}
            titleTx="cycleForm.pondIdPlaceholder"
            isUseSearch
          />
        </FormControl>
        <ModelField
          key={`${farmId}-${pondId}-cycle_id`}
          colorScheme={colorScheme}
          name="cycle_id"
          formControlProps={{ flex: 1, labelTx: 'cycle', isDisabled: !pondId }}
          modelPickerProps={{
            Model: Cycle,
            dataLabel: "started_at",
            fetchData: (model, search) => (
              model.fetch({
                pond_id__eq: pondId,
                sort: 'started_at,desc|pond_name_alpha_numeric'
              }).then((models) => models.filter((m) => (
                `${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
              ).toLowerCase().includes(search.toLowerCase())))
            ),
            renderItemText: (item) => item && `${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
            renderSelectedItemText: (item) => item && `${moment(item.started_at).format("DD MMM YYYY")}`,
            value: cycleId && (cycleId || null),
            isDisabled: (initiating || !pondId),
            onValueChange: onCycleChange,
            titleTx: "cycleForm.idPlaceholder",
            placeholderTx: 'cycleForm.idPlaceholder',
            InputRightElement: initiating && <Spinner mr="9px"></Spinner>
          }}
        />
      </View>
      {/* {!!pondStore && (
        <LazyRender loading={(queryingCycle || false)} renderPlaceholder={() => <Text alignSelf="center" mt="150px" text="Loading..." colorScheme={colorScheme} />}>
          {source && <JalaWebView source={{ uri: source }} style={ROOT} startInLoadingState />}
        </LazyRender>
      )} */}
      {cycleId ? (
        <LazyRender loading={(queryingCycle || false)} renderPlaceholder={LoadingState}>
          {source && <JalaWebView
            source={{ uri: source }}
            style={ROOT}
            startInLoadingState
          />}
        </LazyRender>
      ): <EmptyState colorScheme={colorScheme} />}
    </Screen>
  )
})
